import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { type Device } from "../Device";
import { type CreateDeviceRequest } from "../DeviceService";

import { deviceQueryKeys } from "./deviceQueryKeys";
import { getQueryKey as getAllDevicesByOrgKey } from "./useAllDevicesByOrg";

interface CreateDeviceParams {
  orgId: string;
  request: CreateDeviceRequest;
}

export function useCreateDevice() {
  const { devices } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<Device, Error, CreateDeviceParams>({
    mutationFn: ({ orgId, request }) =>
      devices.createDevice(request, { orgId }),
    onSuccess: (createdDevice, { orgId }) => {
      // A device was added, so every per-org listing of devices — the grid and
      // the all-devices selector roster — is now stale.
      void queryClient.invalidateQueries({
        queryKey: deviceQueryKeys.lists(orgId),
      });

      void queryClient.invalidateQueries({
        queryKey: getAllDevicesByOrgKey(orgId),
      });

      // The response is the newest state of the record, so seed the detail
      // cache with it rather than making the device page refetch.
      queryClient.setQueryData(
        ["device", createdDevice.device_id],
        createdDevice,
      );
    },
  });
}
